// @ts-check
import AdyenCheckout from '@adyen/adyen-web';

// Type definitions for Adyen

/**
 * @typedef {Object} AdyenConfig
 * @property {Object} adyen
 * @property {boolean} adyen.enabled
 * @property {string} adyen.environment
 * @property {string} adyen.publicKey
 * @property {Object} lang
 * @property {string} lang.current
 * @property {string} lang.default
 */

/**
 * Adyen engine factory
 * @returns {Object} Adyen engine interface
 */
function Adyen() {
  let adyenInstance = null;

  /**
   * Returns the Adyen instance
   * @returns {Object|null} Adyen instance
   */
  function getAdyen() {
    return adyenInstance;
  }

  /**
   * Returns the last valid Adyen component instance
   * @returns {Object} Adyen component instance
   */
  function getAdyenInstance() {
    const instances = adyenInstance.components;
    const lastInstance = instances[instances.length - 1];
    const validInstances = instances.filter((instance) => instance.isValid);

    if (validInstances.length > 0) {
      const lastValidInstance = validInstances[validInstances.length - 1];

      lastInstance.setState(lastValidInstance.state);
      return lastInstance;
    }

    return lastInstance;
  }

  /**
   * Loads Adyen instance
   * @function initialize
   * @param {AdyenConfig} config
   * @returns {void}
   *
   * @example
   * ```js
   * adyen.initialize({
   *  clientKey: '100000000000123456789',
   * });
   * ```
   */
  const initialize = (config) => {
    const { enabled, environment, publicKey: clientKey } = config.adyen || {};
    if (!enabled) return;
    if (!Adyen) return;

    adyenInstance = new AdyenCheckout({
      locale: config.lang.current || config.lang.default,
      clientKey,
      environment,
    });
  };

  /**
   * Creates an Apple Pay component instance
   * @param {Object} config - Apple Pay specific configuration
   * @returns {Object|null} Apple Pay component instance
   */
  const createApplePay = (config = {}) => {
    if (!adyenInstance) {
      console.warn('Adyen instance not initialized. Call initialize() first.');
      return null;
    }

    try {
      const applePayComponent = adyenInstance.create('applepay', config);
      return applePayComponent;
    } catch (error) {
      console.error('Error creating Apple Pay component:', error);
      return null;
    }
  };

  /**
   * Creates a Google Pay component instance
   * @param {Object} config - Google Pay specific configuration
   * @returns {Object|null} Google Pay component instance
   */
  const createGooglePay = (config = {}) => {
    if (!adyenInstance) {
      console.warn('Adyen instance not initialized. Call initialize() first.');
      return null;
    }

    try {
      const googlePayComponent = adyenInstance.create('googlepay', config);
      return googlePayComponent;
    } catch (error) {
      console.error('Error creating Google Pay component:', error);
      return null;
    }
  };

  return { initialize, getAdyen, getAdyenInstance, createApplePay, createGooglePay };
}

const adyen = Adyen();
export default adyen;
